import { useEffect, useState } from 'react';
import { RootState, getCurrentUser } from '../../../store';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Hub } from 'aws-amplify/utils';

import { federatedCLMSignIn, getToken } from '../../../services/authentication';
import { downloadFile } from '../../../services/downloadFile';
import { UserRoles, generatePublicFileUrl } from '../../../utils';
import { DataProtectionIcon } from '../../shared/icons/icons';
import SusyButton from '../../shared/buttons/SusyButton';
import { AuthPageWrapper } from '../Wrapper/authPageWrapper/AuthPageWrapper';
import {
    sharedPaths,
    SUSY_AUTH_USER_PROJECT_PATH,
    unauthenticatedPaths,
} from '../../../router/constants/route-paths';
import { SuSyAlert, SusyAlertType } from '../../shared/susyAlert/SusyAlert';
import { FrankfurtSchoolLogo, SusyLogo } from '../icons';
import {
    AwsAmplifyErrorNames,
    AwsAmplifyHubEvents,
} from '../../../definitions/enums/awsAmplify';
import { UserDashboardTabNames } from '../../../components/thesisManagement/UserDashboard';

import './SignIn.css';

const SignIn = () => {
    const [showClmError, setShowClmError] = useState(false);

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const currentUser = useSelector(
        (state: RootState) => state.auth.currentUser
    );

    const localization = useSelector(
        (state: RootState) =>
            state.localization.localizationStrings.susy.authentication.signIn
    );

    const afterSignInHandler = async () => {
        const token = await getToken();
        try {
            if (token) {
                await dispatch(getCurrentUser());
            }
        } catch (err: any) {
            setShowClmError(true);
        }
    };

    const navigateAuthenticatedUser = (userRole: UserRoles) => {
        const savedPath = localStorage.getItem(SUSY_AUTH_USER_PROJECT_PATH);

        if (savedPath) {
            if (!savedPath.includes(userRole)) {
                navigate(savedPath);
            }

            localStorage.removeItem(SUSY_AUTH_USER_PROJECT_PATH);
        } else {
            const pathToNavigate = `${userRole}/${sharedPaths.dashboard}${
                userRole == UserRoles.user.toLowerCase()
                    ? '/' + UserDashboardTabNames.yourProjects
                    : ''
            }`;
            navigate(pathToNavigate);
        }
    };

    useEffect(() => {
        const awsAmplifyEventsListenerRemoval = Hub.listen('auth', (data) => {
            const { payload } = data;
            if (payload.event === AwsAmplifyHubEvents.signIn) {
                afterSignInHandler();
            }
        });

        return () => {
            awsAmplifyEventsListenerRemoval();
        };
    }, []);

    useEffect(() => {
        if (!currentUser) {
            return;
        }

        const userRole = currentUser.userRole.key.toLowerCase() as UserRoles;
        navigateAuthenticatedUser(userRole);
    }, [currentUser]);

    const clmSignInHandler = async () => {
        setShowClmError(false);
        try {
            await federatedCLMSignIn();
        } catch (e: any) {
            if (
                e.name ===
                AwsAmplifyErrorNames.UserAlreadyAuthenticatedException
            ) {
                dispatch(getCurrentUser());
            } else {
                setShowClmError(true);
            }
        }
    };

    const susySignInHandler = () => {
        navigate(unauthenticatedPaths.loginWithEmailAndPassword);
    };

    const downloadDataProtection = () => {
        downloadFile(
            generatePublicFileUrl(localization.dataProtection.fileOriginalName),
            localization.dataProtection.fileOriginalName
        );
    };

    return (
        <AuthPageWrapper
            title={localization.WelcomeMessageTitle}
            subtitle={localization.WelcomeMessageSubtitle}
        >
            <>
                {showClmError && (
                    <SuSyAlert
                        type={SusyAlertType.WARNING}
                        text={
                            localization.errorMessages
                                .clmAccountExistsButSusyAccountDoesntExist
                        }
                        hideIcon={true}
                        className='mb-4'
                    />
                )}

                <div className='login-to-susy-caption text-center mt-4 mb-4'>
                    {localization.logInTitle}
                </div>

                <SusyButton
                    text={localization.clmLogin}
                    onClickHandler={clmSignInHandler}
                    classes='login-options-button clm-brand mb-2'
                    fullWidth
                    icon={<FrankfurtSchoolLogo />}
                />
                <div
                    data-testid={'clm-account-text'}
                    className={'login-account-info-text text-center'}
                >
                    {localization.clmLoginInfoLabel}
                </div>
                <div
                    className={
                        'or-divider d-flex justify-content-center mb-3 mt-3'
                    }
                >
                    {localization.or}
                </div>
                <SusyButton
                    text={localization.emailAndPasswordLogin}
                    onClickHandler={susySignInHandler}
                    classes='login-options-button mb-2'
                    fullWidth
                    icon={<SusyLogo />}
                />
                <div
                    data-testid={'susy-account-text'}
                    className={'login-account-info-text text-center'}
                >
                    {localization.emailAndPasswordLoginInfoLabel}
                </div>

                <div
                    className='d-flex align-items-center justify-content-center mt-4'
                    onClick={downloadDataProtection}
                >
                    <DataProtectionIcon />
                    <div className='text-center ml-2 underline-hover'>
                        {localization.dataProtection.fileName}
                    </div>
                </div>
            </>
        </AuthPageWrapper>
    );
};

export default SignIn;
